/**
 * Heater clearance layout — derives the boxes the heater-clearance domain
 * check needs (heater, room, benches, shields) from a BuildInput, using
 * the exact same placement formulas as the square/two-room engines.
 *
 * Any change to heater/bench placement in squareSauna.ts or
 * twoRoomSauna.ts must be mirrored here — heaterClearanceLayout.test.ts
 * guards against drift by comparing against the built meshes.
 *
 * Barrel saunas are not supported (deprecated engine, issue 071).
 */

import { dividerHeaterCenterZM } from "../domain/partition/partitionSplit";
import { calculateHeaterPlacement } from "../domain/chimney/calculateHeaterPlacement";
import type { HeaterRef } from "../domain/geometry/buildingDimensions";
import type {
	ClearanceBox,
	HeaterClearanceObstacle,
	ShieldedDirections,
} from "../domain/heater/calculateHeaterClearance";
// The bench dimensions have ONE owner (issue 130). This module used to keep its
// own copies — and one of them, the 35 mm slat, had already drifted from the
// 28×90 the BOM buys.
import {
	type BenchAnchor,
	type BenchRun,
	createInteriorLayout,
	DEFAULT_BENCH_ANCHOR,
	type FootprintMm,
	type HeaterPlacement,
	type InteriorLayoutVariantId,
	type InteriorRoom,
	type InteriorWall,
	reconcileInteriorLayout,
	resolveEffectiveLayout,
} from "../domain/interior";
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from "../domain/roof/roofConfig";
import type { SaunaType } from "../types/design";
import {
	getHeaterDepth,
	getHeaterHeight,
	getHeaterSizeMm,
	getHeaterWidth,
} from "./heaterGeometry";
import { benchVerticalExtentM } from "./interiorRenderPlan";
import {
	createSquareBaseBuildContext,
	getInteriorCeilingUndersideYAt,
	type SquareBaseBuildContext,
} from "./squareBaseBuilder";
import {
	getTwoRoomHeaterWall,
	getViewLayoutHeaterCenter,
} from "./twoRoomConstants";
import {
	resolveBenchSupportExcludedWalls,
	resolveGlazedInteriorWalls,
	resolveOpeningAdjustmentOpenings,
} from "./twoRoomOpeningConfig";
import {
	createTwoRoomRoomLayout,
	getHotRoomBenchPlacement,
	resolvePartitionSplit,
	resolveTwoRoomConfig,
} from "./twoRoomSauna";
import type { BuildInput } from "./types";

export interface HeaterClearanceLayout {
	heaterBox: ClearanceBox;
	roomInnerBox: ClearanceBox;
	shieldedDirections: ShieldedDirections;
	obstacles: HeaterClearanceObstacle[];
}

// Mirrors buildSquareInterior / buildTwoRoomInterior placement constants.
const HEATER_WALL_MARGIN_M = 0.15;
/** Gap between the floor plane and the heater underside (engine mirror). */
const HEATER_FLOOR_GAP_M = 0.005;

const BENCH_LABEL_KEY = "domain_heaterClearance_obstacle_bench" as const;
const PARTITION_LABEL_KEY =
	"domain_heaterClearance_obstacle_partition" as const;

/**
 * Does this input describe a two-room sauna? `BuildInput` carries no sauna
 * type, so the only signal is the presence of a two-room field. Exported
 * because `interiorLayoutResolver` must answer the question identically — two
 * copies of the predicate means two different rooms for the same design.
 */
export function isTwoRoomInput(input: BuildInput): boolean {
	return Boolean(
		input.entryWall ||
			input.glazingStyle ||
			input.saunaPosition ||
			input.hasRelaxRoom !== undefined,
	);
}

/**
 * This module's own heater box, restated in the layout's wall-relative terms so
 * the generated bench runs can be clipped against it by the same domain code
 * the renderer and the BOM use.
 *
 * The unit always stands against a long wall here — this module mirrors the
 * engines' placement, and neither engine puts it anywhere else. An AUTHORED
 * heater arrives through `authored.heaterBox` instead and never reaches this.
 */
function layoutHeaterPlacement(
	room: InteriorRoom,
	box: ClearanceBox,
	shortWall?: "front",
): HeaterPlacement {
	if (shortWall) {
		return {
			wall: shortWall,
			offsetMm: ((box.minX + box.maxX) / 2) * 1000 - room.minXMm,
			wallGapMm: Math.max(0, room.maxZMm - box.maxZ * 1000),
		};
	}
	const wall: InteriorWall = box.minX + box.maxX >= 0 ? "right" : "left";
	return {
		wall,
		offsetMm: ((box.minZ + box.maxZ) / 2) * 1000 - room.minZMm,
		wallGapMm: Math.max(
			0,
			wall === "right"
				? room.maxXMm - box.maxX * 1000
				: box.minX * 1000 - room.minXMm,
		),
	};
}

function heaterBoxAt(
	ctx: SquareBaseBuildContext,
	heater: HeaterRef,
	centerX: number,
	centerZ: number,
): ClearanceBox {
	const heaterW = getHeaterWidth(heater);
	const heaterD = getHeaterDepth(heater);
	const heaterH = getHeaterHeight(heater);
	const centerY = ctx.wallBase + heaterH / 2 + HEATER_FLOOR_GAP_M;
	return {
		minX: centerX - heaterW / 2,
		maxX: centerX + heaterW / 2,
		minY: centerY - heaterH / 2,
		maxY: centerY + heaterH / 2,
		minZ: centerZ - heaterD / 2,
		maxZ: centerZ + heaterD / 2,
	};
}

/**
 * A bench obstacle supplied by the caller: the run AS BUILT plus the plan
 * footprint it was resolved to. Passing these makes the clearance check see the
 * layout the user AUTHORED; without them it falls back to the generator's two
 * full-width back-wall runs, which is all there was before issue 130.
 *
 * The whole run travels, not just its tier. A run carrying its own
 * `legHeightMm` is furniture standing on the floor — the renderer reads that off
 * the run — and re-deriving the height from `tier` here put a low authored övre
 * lav near the wall top in the compliance frame, out of the heater's height band
 * and therefore out of the check entirely. See {@link benchVerticalExtentM}.
 */
export interface AuthoredBenchObstacle {
	footprint: FootprintMm;
	run: BenchRun;
}

/**
 * The authored layout's plan geometry: benches AND the heater, as one object.
 *
 * They must arrive together. The benches are clipped around the AUTHORED
 * heater, so measuring them against this module's generated heater box mixes
 * two frames in one check — a run sitting at the authored heater's 50 mm clip
 * margin could land far from the generated box and read as clear.
 */
export interface AuthoredInteriorGeometry {
	benches: AuthoredBenchObstacle[];
	/** Plan footprint of `layout.heater`, from `heaterFootprint()`. */
	heaterBox: { minXMm: number; maxXMm: number; minZMm: number; maxZMm: number };
}

export function deriveHeaterClearanceLayout(
	input: BuildInput,
	authored?: AuthoredInteriorGeometry | null,
): HeaterClearanceLayout | null {
	if (input.roofConfig?.kind === "barrel") {
		return null;
	}

	const twoRoom = isTwoRoomInput(input);
	const saunaType: SaunaType = twoRoom ? "tvarum" : "fyrkantig";
	const roofConfig = input.roofConfig ?? DEFAULT_ROOF_CONFIG_BY_TYPE[saunaType];
	if (roofConfig.kind === "barrel") {
		return null;
	}

	const W = input.dimensions.width;
	const L = input.dimensions.length;
	const heaterType = input.thermalInputs.heaterType;
	const ctx = createSquareBaseBuildContext(W, L, {
		roofConfig,
		wallHeightM: input.wallHeightM,
	});
	const innerW = W - 2 * ctx.sD - 2 * ctx.iL - 2 * ctx.pT;
	const innerL = L - 2 * ctx.sD - 2 * ctx.iL - 2 * ctx.pT;
	const heater = input.thermalInputs;
	const heaterW = getHeaterWidth(heater);
	const heaterD = getHeaterDepth(heater);
	// Heater backs against the right wall by default; panorama moves it to the
	// opposite long wall (see buildTwoRoomInterior / getTwoRoomHeaterWall).
	let heaterCenterX = innerW / 2 - heaterW / 2 - HEATER_WALL_MARGIN_M;
	const obstacles: HeaterClearanceObstacle[] = [];
	// Side shield on the right interior wall is always built; the front
	// shield protects the +Z direction in every non-divider-mounted case.
	const shieldedDirections: ShieldedDirections = { posX: true, posZ: true };

	let heaterCenterZ: number;
	// The hot room's bench wall and Z extent. Everything else about the benches —
	// depths, tiers, where a run stops beside the aggregat — comes out of the
	// interior layout below, not out of this module.
	let benchAnchor: BenchAnchor;
	let benchVariant: InteriorLayoutVariantId = "two-tier";
	// The view layout backs the unit onto the front end wall.
	let heaterShortWall: "front" | undefined;
	let hotRoomZ: { minZ: number; maxZ: number };

	if (twoRoom) {
		const config = resolveTwoRoomConfig(input);
		if (getTwoRoomHeaterWall(config) === "left") {
			heaterCenterX = -heaterCenterX;
			shieldedDirections.posX = false;
			shieldedDirections.negX = true;
		}
		const split = resolvePartitionSplit(
			input.partitionSplit,
			L,
			config.hasRelaxRoom,
		);
		const roomLayout = createTwoRoomRoomLayout(ctx, split, config);
		const placement = calculateHeaterPlacement({
			saunaType,
			widthM: W,
			lengthM: L,
			heaterType,
			heaterModelId: heater.heaterModelId,
			roofKind: roofConfig.kind,
			partitionSplit: roomLayout.hasRelaxRoom
				? roomLayout.saunaLength / L
				: undefined,
			preferredCenterZMm: input.thermalInputs.heaterPlacementZMm,
		});

		const dividerMounted = roomLayout.hasRelaxRoom && heaterType === "wood";
		// Mirror buildTwoRoomInterior: the SAME resolved openings the engine
		// frames — a long entry is relocated off the front wall first.
		const resolvedOpenings = resolveOpeningAdjustmentOpenings({
			...input,
			...config,
		});
		if (roomLayout.viewLayout) {
			const view = getViewLayoutHeaterCenter(
				innerW,
				roomLayout.saunaLength,
				heaterW,
				heaterD,
				HEATER_WALL_MARGIN_M,
			);
			heaterCenterX = view.x;
			heaterCenterZ = roomLayout.saunaCenterZ + view.z;
			heaterShortWall = "front";
			// The engine hangs the side shield on the front wall behind the unit
			// and stands the freestanding plate on its −X side.
			shieldedDirections.posX = false;
			shieldedDirections.negX = true;
			shieldedDirections.posZ = true;
		} else if (dividerMounted) {
			heaterCenterZ = dividerHeaterCenterZM({
				lengthM: ctx.L,
				dividerZM: roomLayout.dividerZ,
				saunaDirection: roomLayout.saunaDirection,
				saunaEndZM:
					roomLayout.saunaDirection === 1
						? roomLayout.saunaMinZ
						: roomLayout.saunaMaxZ,
				heater,
			});
			// The divider shield protects the divider direction; the
			// freestanding front shield is not built in this layout.
			shieldedDirections.posZ = roomLayout.saunaDirection === 1;
			shieldedDirections.negZ = roomLayout.saunaDirection === -1;
		} else {
			heaterCenterZ = placement.selectedCenterZMm / 1000;
			if (roomLayout.hasRelaxRoom) {
				// Unshielded divider wall facing the sauna room.
				const dividerHalfT = ctx.sD / 2 + ctx.iL + ctx.pT;
				obstacles.push({
					kind: "partition",
					labelKey: PARTITION_LABEL_KEY,
					box: {
						minX: -innerW / 2,
						maxX: innerW / 2,
						minY: ctx.wallBase,
						maxY: ctx.wallBase + ctx.H,
						minZ: roomLayout.dividerZ - dividerHalfT,
						maxZ: roomLayout.dividerZ + dividerHalfT,
					},
				});
			} else {
				// Mirror buildTwoRoomInterior: in one-room layouts the heater is
				// nudged clear of a long-wall entry door sharing its wall. Use the
				// SAME resolved openings as the engine — a long entry is relocated
				// from the front wall to the heater wall in resolveOpeningAdjustmentOpenings,
				// so the raw input.openings would never match.
				const doorOnHeaterWall = resolvedOpenings.find(
					(opening) =>
						opening.type === "door" &&
						opening.wall === getTwoRoomHeaterWall(config),
				);
				if (doorOnHeaterWall) {
					const doorMinZ = -L / 2 + doorOnHeaterWall.xMm / 1000;
					const doorMaxZ = doorMinZ + doorOnHeaterWall.widthMm / 1000;
					const clearance = 0.1;
					const overlaps =
						heaterCenterZ - heaterD / 2 < doorMaxZ + clearance &&
						heaterCenterZ + heaterD / 2 > doorMinZ - clearance;
					if (overlaps) {
						const frontZ = doorMaxZ + clearance + heaterD / 2;
						const backZ = doorMinZ - clearance - heaterD / 2;
						if (frontZ + heaterD / 2 <= roomLayout.saunaMaxZ - 0.1) {
							heaterCenterZ = frontZ;
						} else if (backZ - heaterD / 2 >= roomLayout.saunaMinZ + 0.1) {
							heaterCenterZ = backZ;
						}
					}
				}
			}
		}

		// Same owner the renderer hangs its bench meshes on, so the obstacle boxes
		// cannot describe benches at a different end of the room.
		const benchPlacement = getHotRoomBenchPlacement(roomLayout, {
			openings: resolvedOpenings,
			lengthM: L,
		});
		benchAnchor = benchPlacement;
		benchVariant = benchPlacement.variant;
		hotRoomZ = roomLayout.hasRelaxRoom
			? { minZ: roomLayout.saunaMinZ, maxZ: roomLayout.saunaMaxZ }
			: { minZ: -innerL / 2, maxZ: innerL / 2 };
	} else {
		const placement = calculateHeaterPlacement({
			saunaType,
			widthM: W,
			lengthM: L,
			heaterType,
			heaterModelId: heater.heaterModelId,
			roofKind: roofConfig.kind,
			preferredCenterZMm: input.thermalInputs.heaterPlacementZMm,
		});
		heaterCenterZ = placement.selectedCenterZMm / 1000;
		benchAnchor = DEFAULT_BENCH_ANCHOR;
		hotRoomZ = { minZ: -innerL / 2, maxZ: innerL / 2 };
	}

	const generatedHeaterBox = heaterBoxAt(
		ctx,
		heater,
		heaterCenterX,
		heaterCenterZ,
	);

	const hotRoom: InteriorRoom = {
		id: "sauna",
		minXMm: (-innerW / 2) * 1000,
		maxXMm: (innerW / 2) * 1000,
		minZMm: hotRoomZ.minZ * 1000,
		maxZMm: hotRoomZ.maxZ * 1000,
		benchSupportExcludedWalls: resolveBenchSupportExcludedWalls(input),
		glazedWalls: resolveGlazedInteriorWalls(input),
	};

	// A bench's plan footprint plus the Y extent the RENDERER gives that run.
	// One expression for BOTH the authored and the generated path, and the
	// height comes from the run itself — `tier` alone cannot answer it, because
	// a run may carry its own leg height and then stands on the floor.
	const benchBoxFromRun = (
		footprint: FootprintMm,
		run: BenchRun,
	): ClearanceBox => {
		const { minY, maxY } = benchVerticalExtentM(
			run,
			ctx.wallBase,
			ctx.H,
			hotRoom,
		);
		return {
			minX: footprint.minXMm / 1000,
			maxX: footprint.maxXMm / 1000,
			minY,
			maxY,
			minZ: footprint.minZMm / 1000,
			maxZ: footprint.maxZMm / 1000,
		};
	};

	// An authored layout replaces BOTH halves of the generated pair: its runs
	// are already clipped around the AUTHORED heater by `resolveEffectiveLayout`,
	// and the heater box must be that same authored placement — the benches and
	// the heater have to share one frame or a violation can hide between them.
	// Heights stay derived (the layout is plan-view): the heater keeps the
	// unit's real Y extent, the benches their tier's.
	if (authored) {
		const heaterBox: ClearanceBox = {
			minX: authored.heaterBox.minXMm / 1000,
			maxX: authored.heaterBox.maxXMm / 1000,
			minY: generatedHeaterBox.minY,
			maxY: generatedHeaterBox.maxY,
			minZ: authored.heaterBox.minZMm / 1000,
			maxZ: authored.heaterBox.maxZMm / 1000,
		};
		for (const bench of authored.benches) {
			obstacles.push({
				kind: "bench",
				labelKey: BENCH_LABEL_KEY,
				box: benchBoxFromRun(bench.footprint, bench.run),
			});
		}
		// The renderer's shields stand at the GENERATED heater. If the authored
		// unit has moved away from them they protect nothing — claiming their
		// exemptions would wave through a wall the shield is not in front of.
		const movedM = Math.max(
			Math.abs(heaterBox.minX - generatedHeaterBox.minX),
			Math.abs(heaterBox.maxX - generatedHeaterBox.maxX),
			Math.abs(heaterBox.minZ - generatedHeaterBox.minZ),
			Math.abs(heaterBox.maxZ - generatedHeaterBox.maxZ),
		);
		return {
			heaterBox,
			roomInnerBox: buildRoomInnerBox(ctx, innerW, innerL, heaterBox),
			shieldedDirections: movedM > 0.001 ? {} : shieldedDirections,
			obstacles,
		};
	}

	const heaterBox = generatedHeaterBox;

	// The GENERATED benches come out of the interior layout, exactly like the
	// authored ones above and like the ones the renderer draws. This module used
	// to carry its own second bench model — its own depths, its own
	// `benchUpperZ`/`benchLowerZ`, and a second call to `clipBenchSpanToHeater`.
	// Two clippers with the same constants still drift; one clipper cannot. The
	// pure domain and `interiorRenderPlan` are both leaves, so reading them here
	// closes no import cycle (the resolver, which imports twoRoomSauna, would).
	const heaterSize = getHeaterSizeMm(heater);
	const generatedLayout = reconcileInteriorLayout(
		createInteriorLayout(hotRoom, {
			variant: benchVariant,
			heater: layoutHeaterPlacement(hotRoom, heaterBox, heaterShortWall),
			benchAnchor,
		}),
		[hotRoom],
		heaterSize,
	);
	for (const { effective, footprint } of resolveEffectiveLayout(
		generatedLayout,
		hotRoom,
		heaterSize,
	).runs) {
		obstacles.push({
			kind: "bench",
			labelKey: BENCH_LABEL_KEY,
			box: benchBoxFromRun(footprint, effective),
		});
	}

	return {
		heaterBox,
		roomInnerBox: buildRoomInnerBox(ctx, innerW, innerL, heaterBox),
		shieldedDirections,
		obstacles,
	};
}

function buildRoomInnerBox(
	ctx: SquareBaseBuildContext,
	innerW: number,
	innerL: number,
	heaterBox: ClearanceBox,
): ClearanceBox {
	return {
		minX: -innerW / 2,
		maxX: innerW / 2,
		minY: ctx.wallBase,
		maxY: getInteriorCeilingUndersideYAt(
			ctx,
			(heaterBox.minX + heaterBox.maxX) / 2,
			(heaterBox.minZ + heaterBox.maxZ) / 2,
		),
		minZ: -innerL / 2,
		maxZ: innerL / 2,
	};
}
